import { Body, Controller, Delete, ForbiddenException, Get, HttpCode, Param, Patch, Post, Query, Req, UseGuards } from '@nestjs/common';
import {
  ApiBearerAuth,
  ApiBody,
  ApiOperation,
  ApiParam,
  ApiQuery,
  ApiResponse,
  ApiSecurity,
  ApiTags,
} from '@nestjs/swagger';
import {
  CreateProductRequest,
  ProductDetail,
  ProductListQuery,
  ProductListResponse,
  ProductStatsResponse,
  ProductSummary,
  UpdateInventoryRequest,
  UpdateProductRequest,
} from '@cart/shared-types';
import { AdminAccessGuard } from '../auth/admin-access.guard';
import { AuthGuard } from '../auth/auth.guard';
import { ProductsService } from './products.service';
import { ProductStoreScopeService } from './product-store-scope.service';
import { AuthenticatedRequest } from '../auth/auth.guard';

/**
 * The guard on these routes always populates `req.user`; the type does not say
 * so. Throwing here rather than asserting keeps the impossible case honest.
 */
function requireUserId(req: AuthenticatedRequest): string {
  const id = req.user?.id;
  if (!id) throw new ForbiddenException('Authentication is required');
  return id;
}

type RawProductListQuery = Record<string, string | undefined>;

@ApiTags('Products')
@ApiSecurity('store-id')
@Controller('products')
export class ProductsController {
  constructor(private readonly productsService: ProductsService) {}

  @Get()
  @ApiOperation({ summary: 'List active products with optional filters' })
  @ApiQuery({ name: 'search', required: false, type: String, description: 'Full-text search on name' })
  @ApiQuery({ name: 'category', required: false, type: String, description: 'Filter by category slug' })
  @ApiQuery({ name: 'minPrice', required: false, type: Number, description: 'Min price in pence/cents' })
  @ApiQuery({ name: 'maxPrice', required: false, type: Number, description: 'Max price in pence/cents' })
  @ApiQuery({ name: 'inStock', required: false, type: Boolean, description: 'Only show in-stock items' })
  @ApiQuery({ name: 'sort', required: false, enum: ['price-asc', 'price-desc', 'newest'] })
  @ApiQuery({ name: 'page', required: false, type: Number, description: 'Page number (default 1)' })
  @ApiQuery({ name: 'limit', required: false, type: Number, description: 'Items per page — max 100 (default 20)' })
  @ApiResponse({ status: 200, description: 'Returns paginated product list with total count and page metadata' })
  list(@Query() query: RawProductListQuery): Promise<ProductListResponse> {
    return this.productsService.list({
      search: query.search,
      category: query.category,
      minPrice: this.toOptionalNumber(query.minPrice),
      maxPrice: this.toOptionalNumber(query.maxPrice),
      inStock: this.toOptionalBoolean(query.inStock),
      sort: this.toSort(query.sort),
      page: this.toOptionalNumber(query.page),
      limit: this.toOptionalNumber(query.limit),
    });
  }

  @Get(':slug')
  @ApiOperation({ summary: 'Get product detail by slug' })
  @ApiParam({ name: 'slug', type: String, example: 'blue-denim-jacket' })
  @ApiResponse({ status: 200, description: 'Returns product with description and images' })
  @ApiResponse({ status: 404, description: 'Product not found' })
  detail(@Param('slug') slug: string): Promise<ProductDetail> {
    return this.productsService.detail(slug);
  }

  private toOptionalNumber(value: string | undefined): number | undefined {
    if (value === undefined || value.trim() === '') return undefined;

    return Number(value);
  }

  private toOptionalBoolean(value: string | undefined): boolean | undefined {
    if (value === undefined || value.trim() === '') return undefined;

    return value === 'true';
  }

  private toSort(value: string | undefined): ProductListQuery['sort'] {
    if (value === 'price-asc' || value === 'price-desc' || value === 'newest') return value;

    return undefined;
  }
}

@ApiTags('Admin · Products')
@ApiBearerAuth()
@ApiSecurity('store-id')
@UseGuards(AuthGuard, AdminAccessGuard)
@Controller('admin/products')
export class AdminProductsController {
  constructor(
    private readonly productsService: ProductsService,
    private readonly storeScope: ProductStoreScopeService
  ) {}

  @Get()
  @ApiOperation({ summary: 'List all products including drafts (admin)' })
  @ApiQuery({ name: 'search', required: false, type: String })
  @ApiQuery({ name: 'status', required: false, enum: ['active', 'draft', 'out_of_stock', 'low_stock'] })
  @ApiQuery({
    name: 'sort',
    required: false,
    enum: [
      'price-asc', 'price-desc', 'newest',
      'name-asc', 'name-desc', 'sku-asc', 'sku-desc',
      'category-asc', 'category-desc', 'updated-asc', 'updated-desc',
    ],
  })
  @ApiQuery({ name: 'categoryIds', required: false, type: String, description: 'Comma-separated category ids' })
  @ApiQuery({ name: 'taxRateBps', required: false, type: Number })
  @ApiQuery({ name: 'taxConfigStatus', required: false, enum: ['configured', 'missing'] })
  @ApiQuery({ name: 'page', required: false, type: Number })
  @ApiQuery({ name: 'limit', required: false, type: Number })
  @ApiResponse({ status: 200, description: 'Returns all products including inactive' })
  adminList(@Query() query: RawProductListQuery): Promise<ProductListResponse> {
    return this.productsService.adminList({
      search: query.search,
      category: query.category,
      categoryIds: this.toCategoryIds(query.categoryIds),
      status: this.toStatus(query.status),
      taxMode: this.toTaxMode(query.taxMode),
      taxProfileId: query.taxProfileId,
      taxRateBps: this.toOptionalNumber(query.taxRateBps),
      taxConfigStatus: this.toTaxConfigStatus(query.taxConfigStatus),
      brandId: query.brandId,
      sort: this.toSort(query.sort),
      page: this.toOptionalNumber(query.page),
      limit: this.toOptionalNumber(query.limit),
    });
  }

  @Get('stats')
  @ApiOperation({ summary: "Catalog-wide status counts for the admin header's stat cards" })
  @ApiResponse({ status: 200, description: 'Returns total/active/draft/outOfStock/lowStock/archived counts' })
  adminStats(@Query() query: RawProductListQuery): Promise<ProductStatsResponse> {
    return this.productsService.adminStats({
      search: query.search,
      category: query.category,
      categoryIds: this.toCategoryIds(query.categoryIds),
      taxMode: this.toTaxMode(query.taxMode),
      taxProfileId: query.taxProfileId,
      taxRateBps: this.toOptionalNumber(query.taxRateBps),
      taxConfigStatus: this.toTaxConfigStatus(query.taxConfigStatus),
      brandId: query.brandId,
    });
  }

  @Get(':id')
  @ApiOperation({ summary: 'Get admin product detail by id' })
  @ApiParam({ name: 'id', type: String })
  @ApiResponse({ status: 200, description: 'Returns product with description and images' })
  @ApiResponse({ status: 404, description: 'Product not found' })
  adminDetail(@Param('id') productId: string): Promise<ProductDetail> {
    return this.productsService.adminDetail(productId);
  }


  /**
   * Stores this user may create a product in.
   *
   * A merchant owns the organization, so every store under it. A store manager
   * holds explicit assignments, and those are the whole of their reach — the
   * add-product screen offers exactly what the API would accept, rather than
   * offering everything and refusing on save.
   */
  @Get('creatable-stores')
  @ApiOperation({ summary: 'Stores the signed-in user can add products to' })
  creatableStores(@Req() req: AuthenticatedRequest) {
    return this.storeScope.creatableStores(requireUserId(req));
  }

  @Post()
  @ApiOperation({ summary: 'Create a new product (admin)' })
  @ApiBody({
    schema: {
      type: 'object',
      required: ['name', 'slug', 'category', 'price', 'stock'],
      properties: {
        name: { type: 'string', example: 'Blue Denim Jacket' },
        slug: { type: 'string', example: 'blue-denim-jacket' },
        category: { type: 'string', description: 'Category slug', example: 'jackets' },
        price: { type: 'integer', description: 'Price in pence/cents', example: 4999 },
        stock: { type: 'integer', minimum: 0, example: 50 },
        imageUrl: { type: 'string', example: 'https://example.com/jacket.jpg' },
        unitOfMeasure: {
          type: 'string',
          enum: ['piece', 'g', 'ml', 'cm'],
          description: 'Base unit the product is sold in. kg/L/dozen are display conversions — send the base unit.',
          example: 'ml',
        },
        unitQuantity: {
          type: 'integer',
          minimum: 1,
          description: 'Size in base units. 1 L is sent as 1000 with unitOfMeasure "ml".',
          example: 1000,
        },
      },
    },
  })
  @ApiResponse({ status: 201, description: 'Product created' })
  @ApiResponse({ status: 400, description: 'Validation error' })
  @ApiResponse({ status: 409, description: 'Slug already exists' })
  create(
    @Body() body: CreateProductRequest,
    @Req() req: AuthenticatedRequest
  ): Promise<ProductSummary> {
    // storeIds is validated against what this user may actually reach; see
    // ProductStoreScopeService.
    return this.productsService.create(body, {
      userId: requireUserId(req),
      storeIds: body.storeIds,
    });
  }

  @Patch(':id')
  @ApiOperation({ summary: 'Update product fields (admin)' })
  @ApiParam({ name: 'id', type: String })
  @ApiBody({
    schema: {
      type: 'object',
      properties: {
        name: { type: 'string' },
        description: { type: 'string' },
        price: { type: 'integer', description: 'Price in pence/cents' },
        active: { type: 'boolean' },
        categorySlug: { type: 'string' },
        unitOfMeasure: { type: 'string', enum: ['piece', 'g', 'ml', 'cm'] },
        unitQuantity: { type: 'integer', minimum: 1 },
      },
    },
  })
  @ApiResponse({ status: 200, description: 'Product updated' })
  @ApiResponse({ status: 404, description: 'Product not found' })
  update(
    @Param('id') productId: string,
    @Body() body: UpdateProductRequest
  ): Promise<ProductSummary> {
    return this.productsService.update(productId, body);
  }

  @Patch(':id/inventory')
  @ApiOperation({ summary: 'Update product stock level (admin)' })
  @ApiParam({ name: 'id', type: String })
  @ApiBody({
    schema: {
      type: 'object',
      required: ['stock'],
      properties: {
        stock: { type: 'integer', minimum: 0, example: 100 },
      },
    },
  })
  @ApiResponse({ status: 200, description: 'Stock updated' })
  @ApiResponse({ status: 404, description: 'Product not found' })
  updateInventory(
    @Param('id') productId: string,
    @Body() body: UpdateInventoryRequest
  ): Promise<ProductSummary> {
    return this.productsService.updateInventory(productId, body);
  }

  @Delete(':id')
  @HttpCode(200)
  @ApiOperation({ summary: 'Soft-deactivate a product — sets active=false (admin)' })
  @ApiParam({ name: 'id', type: String })
  @ApiResponse({ status: 200, description: 'Product deactivated (active=false), still in DB' })
  @ApiResponse({ status: 404, description: 'Product not found' })
  deactivate(@Param('id') productId: string): Promise<ProductSummary> {
    return this.productsService.deactivate(productId);
  }

  private toOptionalNumber(value: string | undefined): number | undefined {
    if (value === undefined || value.trim() === '') return undefined;
    return Number(value);
  }

  private toSort(value: string | undefined): ProductListQuery['sort'] {
    const valid: ProductListQuery['sort'][] = [
      'price-asc', 'price-desc', 'newest',
      'name-asc', 'name-desc', 'sku-asc', 'sku-desc',
      'category-asc', 'category-desc', 'updated-asc', 'updated-desc',
    ];
    return valid.includes(value as ProductListQuery['sort']) ? (value as ProductListQuery['sort']) : undefined;
  }

  private toStatus(value: string | undefined): ProductListQuery['status'] {
    if (value === 'active' || value === 'draft' || value === 'out_of_stock' || value === 'low_stock') return value;
    return undefined;
  }

  private toTaxMode(value: string | undefined): ProductListQuery['taxMode'] {
    if (value === 'predefined' || value === 'custom') return value;
    return undefined;
  }

  private toTaxConfigStatus(value: string | undefined): ProductListQuery['taxConfigStatus'] {
    if (value === 'configured' || value === 'missing') return value;
    return undefined;
  }

  private toCategoryIds(value: string | undefined): string[] | undefined {
    if (!value?.trim()) return undefined;
    const ids = value.split(',').map((id) => id.trim()).filter(Boolean);
    return ids.length ? ids : undefined;
  }
}
